import { observer } from 'mobx-react';
import React from 'react';
import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Skeleton } from 'primereact/skeleton';
import { FileText, Mail, Clock, Plus } from 'lucide-react';

import { useStore } from 'hooks/useStore';
import { OOCSubmission, OOCTabType, OOC_STATUS_OPTIONS, formatOOCDate } from '../oocForecast.type';
import { isCurrentUser } from 'utils/userUtils';
import {
  useOOCDashboardLogic,
  getStatusBadgeClass,
  getStatusLabel,
  CONTRACT_TYPE_OPTIONS,
  SummaryCardConfig,
} from './oocDashboard.logic';
import styles from './oocDashboard.module.scss';

const ICON_MAP: Record<SummaryCardConfig['iconType'], React.ReactNode> = {
  fileText: <FileText size={20} />,
  mail: <Mail size={20} />,
  clock: <Clock size={20} />,
};

const ActionCell = observer(({ row, loggedInUserName }: { row: OOCSubmission; loggedInUserName: string }) => {
  const { OOCStore } = useStore();
  if (OOCStore.savingSubmissionId === row.id) {
    return <Button label="Saving..." size="small" className={`${styles.actionBtn} ${styles.editBtn}`} disabled />;
  }
  if (row.status === 'draft' && isCurrentUser(loggedInUserName, row.submittedBy)) {
    return <Button label="Edit" size="small" className={`${styles.actionBtn} ${styles.editBtn}`} onClick={() => OOCStore.openSubmissionForEdit(row.id)} />;
  }
  return <Button label="View" size="small" className={`${styles.actionBtn} ${styles.viewBtn}`} onClick={() => OOCStore.openSubmissionForEdit(row.id)} />;
});

const SEARCH_LOADING_SENTINEL = '__searching__';

const OOCDashboard = observer(() => {
  const { OOCStore, loggedInUserName, displayedSubmissions, handleCardClick, handleCreateNew, handleSearch, handleSuggestionComplete, handleSuggestionSelect, summaryCardConfigs } = useOOCDashboardLogic();

  const autoCompleteSuggestions: string[] = OOCStore.isFetchingSuggestions
    ? [SEARCH_LOADING_SENTINEL]
    : OOCStore.searchSuggestions;

  const suggestionItemTemplate = (item: string) => {
    if (item === SEARCH_LOADING_SENTINEL) {
      return (
        <div className={styles.searchingItem}>
          <i className="pi pi-spin pi-spinner" />
          <span>Searching...</span>
        </div>
      );
    }
    return <span>{item}</span>;
  };

  const oocIdTemplate = (row: OOCSubmission) => (
    <span className={styles.oocIdCell} onClick={() => OOCStore.openSubmissionForEdit(row.id)}>
      {row.id}
    </span>
  );

  const contractTypesTemplate = (row: OOCSubmission) => (
    <div className={styles.contractChips}>
      {row.contractTypes.map((ct) => (
        <span key={ct} className={styles.contractChip}>{ct}</span>
      ))}
    </div>
  );

  const statusTemplate = (row: OOCSubmission) => (
    <span className={`${styles.statusBadge} ${getStatusBadgeClass(row.status, styles)}`}>
      {getStatusLabel(row.status)}
    </span>
  );

  const dateTemplate = (row: OOCSubmission) => formatOOCDate(row.submissionDate);
  const actionTemplate = (row: OOCSubmission) => <ActionCell row={row} loggedInUserName={loggedInUserName} />;

  if (OOCStore.isLoading && OOCStore.submissions.length === 0) {
    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.skeletonCards}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className={styles.skeletonCard} />
          ))}
        </div>
        <Skeleton height="3rem" />
        <Skeleton height="20rem" />
      </div>
    );
  }

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.summaryCards}>
        {summaryCardConfigs.map((card) => (
          <div
            key={card.key}
            className={`${styles.summaryCard} ${
              card.key !== 'history' && OOCStore.activeCardFilter === card.key ? styles.active : ''
            }`}
            onClick={() => handleCardClick(card.key)}
          >
            <div className={`${styles.cardIcon} ${styles[card.colorClass]}`}>
              {ICON_MAP[card.iconType]}
            </div>
            <div className={styles.cardInfo}>
              <span className={styles.cardCount}>{card.count}</span>
              <span className={styles.cardLabel}>{card.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchWrapper}>
          <AutoComplete
            value={OOCStore.searchQuery}
            suggestions={autoCompleteSuggestions}
            completeMethod={(e) => handleSuggestionComplete(e.query)}
            onChange={(e) => OOCStore.setSearchQuery(String(e.value ?? ''))}
            onSelect={(e) => {
              if (e.value !== SEARCH_LOADING_SENTINEL) handleSuggestionSelect(String(e.value));
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search by NDC, OOC ID, Concept, or Molecule..."
            className={styles.searchAutoComplete}
            inputClassName={styles.searchInput}
            itemTemplate={suggestionItemTemplate}
            delay={250}
          />
        </div>
        <Dropdown
          value={OOCStore.statusFilter}
          options={OOC_STATUS_OPTIONS}
          onChange={(e) => OOCStore.setStatusFilter(e.value)}
          className={styles.filterDropdown}
          placeholder="All Statuses"
        />
        <Dropdown
          value={OOCStore.contractTypeFilter}
          options={CONTRACT_TYPE_OPTIONS}
          onChange={(e) => OOCStore.setContractTypeFilter(e.value as OOCTabType | 'all')}
          className={styles.filterDropdown}
          placeholder="All Contract Types"
        />
        <Button
          label="New OOC"
          icon={<Plus size={16} style={{ marginRight: 6 }} />}
          className={styles.newOocBtn}
          onClick={handleCreateNew}
        />
      </div>

      <div className={styles.tableContainer}>
        <DataTable
          value={displayedSubmissions}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          emptyMessage={
            OOCStore.searchResults !== null
              ? `No OOCs found for "${OOCStore.searchQuery}".`
              : 'No OOC submissions found.'
          }
          loading={OOCStore.isLoading || OOCStore.isSearching}
          stripedRows
          size="small"
        >
          <Column header="OOC ID" body={oocIdTemplate} sortable field="id" style={{ minWidth: '120px' }} />
          <Column header="Date" body={dateTemplate} sortable field="submissionDate" style={{ minWidth: '110px' }} />
          <Column header="Submitted By" field="submittedBy" sortable style={{ minWidth: '140px' }} />
          <Column header="Contract Types" body={contractTypesTemplate} style={{ minWidth: '140px' }} />
          <Column header="# NDCs" field="ndcCount" sortable style={{ minWidth: '80px', textAlign: 'center' }} />
          <Column header="Status" body={statusTemplate} sortable field="status" style={{ minWidth: '130px' }} />
          <Column header="Action" body={actionTemplate} style={{ minWidth: '90px' }} />
        </DataTable>
        <div className={styles.tableFooter}>
          Showing {displayedSubmissions.length} of {OOCStore.submissions.length} submissions
        </div>
      </div>
    </div>
  );
});

export default OOCDashboard;
